import React, { useMemo, useRef, useEffect } from 'react';
import { CardBody, Input, Badge } from 'reactstrap';
import {
  COUNTER_ACCENT,
  COUNTER_ICON_BTN_STYLE,
} from '../../../../../shared/offer-domain/constants';
import ActionButton from '../../../../../Components/Common/ActionButton';
import { SkeletonTable } from '../../../../../Components/Common/Skeleton';
import CounterOfferModal from '../CounterOfferModal';
import { getRowKey } from '../../../hooks/modelDeliveryTable/shared';
import {
  formatIncotermRoute,
  formatParcelSize,
  hasCounterOffer,
  isCounterValueChanged,
  isCounterDateChanged,
} from '../shared/utils';
import { buildLpgOfferTableModel } from '../shared/lpgTableModel';
import {
  cellClass,
  headerCellClass,
  renderLpgPortCell,
  createNumberInputRenderer,
} from '../shared/tableRenderHelpers';
import {
  COUNTER_BADGE_STYLE,
  COUNTER_ROW_MARK_STYLE,
  DELIVERY_TABLE_STYLE,
  DELIVERY_MAIN_TABLE_STYLE,
  DeliveryThLabel,
  getLpgColStyles,
  getLpgCounterColStyles,
  withHeaderStyle,
} from '../shared/deliveryTableLayout';
import { getCargoRatioKey } from '../../../../../shared/offer-domain/cargoRatio';

const LPG_UNIT = 'MT';
const getTableColSpan = (showCounterOfferColumn, requiresCargoRatio) => {
  let cols = 8; // expand, incoterm, port1-3, parcel, capable, alpha
  if (requiresCargoRatio) cols += 1;
  if (showCounterOfferColumn) cols += 1;
  return cols;
};

const ModelDeliveryTableLpg = ({
  embedded = false,
  isDeliveryEditable,
  requiresCargoRatio = false,
  isLoading = false,
  searchTerm,
  setSearchTerm,
  filteredItems,
  capableState,
  expandedRows,
  counterModalOpen,
  counterFormData,
  counterHasChanges,
  counterFormErrors,
  counterFormLoading,
  form,
  incotermTypeOptions,
  incotermRouteOptions,
  incotermLocationOptions,
  fobRouteOptions,
  handleInputChange,
  handleCapableChange,
  toggleRowExpand,
  handleCounterFormChange,
  setCounterPortSetup,
  handleSaveCounter,
  handleDeleteCounter,
  openCounterModal,
  closeCounterModal,
}) => {
  const handleInputChangeRef = useRef(handleInputChange);
  useEffect(() => {
    handleInputChangeRef.current = handleInputChange;
  }, [handleInputChange]);

  const renderNumberInput = useMemo(
    () =>
      createNumberInputRenderer({
        isDeliveryEditable,
        handleInputChange: (e, rowKey, field, type, row) =>
          handleInputChangeRef.current(e, rowKey, field, type, row),
      }),
    [isDeliveryEditable]
  );

  const tableRows = useMemo(
    () => buildLpgOfferTableModel(filteredItems, LPG_UNIT),
    [filteredItems]
  );

  const counterByGroupId = useMemo(() => {
    const map = {};
    tableRows.forEach(row => {
      map[row.groupId] = map[row.groupId] || hasCounterOffer(row.data);
    });
    return map;
  }, [tableRows]);

  const showCounterOfferColumn = useMemo(() => {
    if (!isDeliveryEditable) return false;
    const groupIds = [...new Set(tableRows.map(row => row.groupId))];
    return groupIds.some(groupId => {
      if (counterByGroupId[groupId]) return false;
      return tableRows
        .filter(row => row.groupId === groupId)
        .some(row => !(capableState[getRowKey(row.data)] || false));
    });
  }, [isDeliveryEditable, tableRows, counterByGroupId, capableState]);

  const col = getLpgColStyles({
    requiresCargoRatio,
    showCounterOfferColumn,
  });
  const colSpan = getTableColSpan(showCounterOfferColumn, requiresCargoRatio);

  const renderCounterOfferRow = (data, groupMeta) => {
    const nestedCol = getLpgCounterColStyles({
      requiresCargoRatio,
      showActions: isDeliveryEditable,
    });
    const incotermRoute = formatIncotermRoute(
      data.counterIncoterm,
      data.counterRoute
    );
    const originalIncotermRoute = formatIncotermRoute(
      data.incoterm,
      data.route
    );
    const incotermRouteChanged = incotermRoute !== originalIncotermRoute;

    const parcelByKey = new Map();
    (filteredItems || []).forEach(item => {
      if (
        String(item.quantityIncotermId ?? '') !==
        String(data.quantityIncotermId ?? '')
      ) {
        return;
      }
      const parcelKey = String(
        item.parcelSizeLabel ?? item.parcelSize ?? item.deliveryBasis ?? ''
      );
      if (!parcelKey) return;
      const current = parcelByKey.get(parcelKey);
      if (
        !current ||
        (!current.quantityInformationDetailCounterId &&
          item.quantityInformationDetailCounterId)
      ) {
        parcelByKey.set(parcelKey, item);
      }
    });

    const masterParcelOrder = groupMeta?.parcelSizes ?? [];
    const parcelItems = [];
    if (masterParcelOrder.length) {
      const usedKeys = new Set();
      masterParcelOrder.forEach(label => {
        const key = String(label);
        if (parcelByKey.has(key)) {
          parcelItems.push(parcelByKey.get(key));
          usedKeys.add(key);
          return;
        }
        for (const [parcelKey, item] of parcelByKey.entries()) {
          if (usedKeys.has(parcelKey)) continue;
          const itemLabel = String(
            item.parcelSizeLabel ??
              formatParcelSize(item.parcelSize ?? item.deliveryBasis, 'MT')
          );
          if (itemLabel === key) {
            parcelItems.push(item);
            usedKeys.add(parcelKey);
            break;
          }
        }
      });
      parcelByKey.forEach((item, key) => {
        if (!usedKeys.has(key)) parcelItems.push(item);
      });
    } else {
      parcelByKey.forEach(item => parcelItems.push(item));
    }

    if (!parcelItems.length) {
      if (masterParcelOrder.length) {
        masterParcelOrder.forEach(label => {
          parcelItems.push({ ...data, parcelSizeLabel: label });
        });
      } else {
        parcelItems.push(data);
      }
    }

    const counterRatiosFromGroup = (filteredItems || []).find(
      item =>
        String(item.quantityIncotermId ?? '') ===
          String(data.quantityIncotermId ?? '') &&
        Array.isArray(item.counterCargoRatios) &&
        item.counterCargoRatios.length
    )?.counterCargoRatios;

    const counterRatios =
      Array.isArray(data.counterCargoRatios) && data.counterCargoRatios.length
        ? data.counterCargoRatios
        : Array.isArray(counterRatiosFromGroup) && counterRatiosFromGroup.length
          ? counterRatiosFromGroup
          : [{ id: null, label: null }];

    const counterRows = [];
    counterRatios.forEach((ratio, ratioIndex) => {
      parcelItems.forEach((parcelItem, parcelIndex) => {
        const ratioKey = getCargoRatioKey(ratio?.id);
        const parcelRatio =
          (parcelItem.counterCargoRatios || []).find(
            entry =>
              String(entry.quantityInformationCargoRatioFK ?? '') ===
                String(parcelItem.quantityInformationCargoRatioId ?? '') ||
              getCargoRatioKey(entry.id) === ratioKey
          ) || null;
        const mergedRatio = parcelRatio
          ? {
              ...ratio,
              ...parcelRatio,
              id: ratio?.id ?? parcelRatio.id,
              label: ratio?.label ?? parcelRatio.label,
              alpha: parcelRatio.alpha ?? parcelItem.counterAlpha ?? '',
            }
          : {
              ...ratio,
              alpha: parcelItem.counterAlpha ?? '',
            };
        counterRows.push({
          ratio: mergedRatio,
          parcelItem,
          showGroupCells: ratioIndex === 0 && parcelIndex === 0,
          showRatioCell: requiresCargoRatio && parcelIndex === 0,
          groupRowSpan: counterRatios.length * parcelItems.length,
          ratioRowSpan: parcelItems.length,
          parcelSizes: groupMeta?.parcelSizes || [],
        });
      });
    });

    return (
      <tr className='bg-light'>
        <td colSpan={colSpan} className='p-0' style={COUNTER_ROW_MARK_STYLE}>
          <div
            className='bg-light-subtle'
            style={{ width: '100%', maxWidth: '100%' }}
          >
            <div className='px-2 py-2 w-100'>
              <table
                aria-label='Counter offer'
                className='table table-sm table-borderless mb-0 align-middle w-100'
                style={DELIVERY_TABLE_STYLE}
              >
                <thead>
                  <tr className='text-muted border-bottom'>
                    <th
                      className={headerCellClass}
                      style={withHeaderStyle(nestedCol.expand)}
                    >
                      <span className='visually-hidden'>Type</span>
                    </th>
                    <th
                      className={headerCellClass}
                      style={withHeaderStyle(nestedCol.incoterm)}
                    >
                      <DeliveryThLabel
                        title='Incoterm - Route'
                        lines={['Incoterm', 'Route']}
                      />
                    </th>
                    <th
                      className={headerCellClass}
                      style={withHeaderStyle(nestedCol.port1)}
                    >
                      Port 1
                    </th>
                    <th
                      className={headerCellClass}
                      style={withHeaderStyle(nestedCol.port2)}
                    >
                      Port 2
                    </th>
                    <th
                      className={headerCellClass}
                      style={withHeaderStyle(nestedCol.port3)}
                    >
                      Port 3
                    </th>
                    {requiresCargoRatio && (
                      <th
                        className={headerCellClass}
                        style={withHeaderStyle(nestedCol.cargoRatio)}
                      >
                        <DeliveryThLabel
                          title='Cargo Ratio'
                          lines={['Cargo', 'Ratio']}
                        />
                      </th>
                    )}
                    <th
                      className={headerCellClass}
                      style={withHeaderStyle(nestedCol.parcel)}
                    >
                      <DeliveryThLabel
                        title='Parcel Size'
                        lines={['Parcel', 'Size']}
                      />
                    </th>
                    <th
                      className={headerCellClass}
                      style={withHeaderStyle(nestedCol.alpha)}
                    >
                      Alpha
                    </th>
                    {isDeliveryEditable && (
                      <th
                        className={headerCellClass}
                        style={withHeaderStyle(nestedCol.counter)}
                      >
                        <span className='visually-hidden'>Actions</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {counterRows.map((row, rowIndex) => (
                    <tr key={`counter-${row.ratio?.id ?? 'x'}-${rowIndex}`}>
                      {row.showGroupCells && (
                        <td
                          rowSpan={row.groupRowSpan}
                          className='ps-1 py-2 align-top'
                          style={{ ...nestedCol.expand, overflow: 'hidden' }}
                        >
                          <span
                            className='badge rounded-pill px-1 py-1 fw-semibold'
                            style={COUNTER_BADGE_STYLE}
                          >
                            Counter
                          </span>
                        </td>
                      )}
                      {row.showGroupCells && (
                        <td
                          rowSpan={row.groupRowSpan}
                          className={`fs-12 py-2 pe-2 align-top text-break ${incotermRouteChanged ? 'fw-semibold' : 'fw-medium'}`}
                          style={{
                            ...nestedCol.incoterm,
                            color: incotermRouteChanged
                              ? COUNTER_ACCENT
                              : undefined,
                          }}
                        >
                          {incotermRoute}
                        </td>
                      )}
                      {row.showGroupCells && (
                        <td
                          rowSpan={row.groupRowSpan}
                          className={`${cellClass} align-top`}
                          style={nestedCol.port1}
                        >
                          {renderLpgPortCell(
                            data.counterFobLocationNames?.length
                              ? null
                              : data.counterLoadingPort,
                            groupMeta?.parcelSizes ?? [],
                            {
                              startDate: data.counterStartDate,
                              endDate: data.counterEndDate,
                              fallbackDate: data.counterAddAld,
                              locationNames: data.counterFobLocationNames,
                              route: data.counterRoute,
                              highlight: {
                                port: isCounterValueChanged(
                                  data.port1 || data.loadingPort,
                                  data.counterLoadingPort
                                ),
                                parcelSize: false,
                                date: isCounterDateChanged(
                                  data.port1StartDate,
                                  data.port1EndDate,
                                  data.counterStartDate,
                                  data.counterEndDate,
                                  data.addAld,
                                  data.counterAddAld
                                ),
                              },
                            }
                          )}
                        </td>
                      )}
                      {row.showGroupCells && (
                        <td
                          rowSpan={row.groupRowSpan}
                          className={cellClass}
                          style={nestedCol.port2}
                        />
                      )}
                      {row.showGroupCells && (
                        <td
                          rowSpan={row.groupRowSpan}
                          className={cellClass}
                          style={nestedCol.port3}
                        />
                      )}
                      {row.showRatioCell && (
                        <td
                          rowSpan={row.ratioRowSpan}
                          className={`${cellClass} fs-13 align-top`}
                          style={nestedCol.cargoRatio}
                        >
                          {row.ratio?.label ? (
                            <span
                              className='badge bg-danger-subtle text-danger border border-danger-subtle fw-semibold rounded-3'
                              style={{ fontSize: '12px' }}
                            >
                              {row.ratio.label}
                            </span>
                          ) : (
                            <span className='text-muted'>-</span>
                          )}
                        </td>
                      )}
                      <td
                        className={`${cellClass} fs-13 fw-semibold align-top`}
                        style={nestedCol.parcel}
                      >
                        {row.parcelItem.parcelSizeLabel ||
                          formatParcelSize(
                            row.parcelItem.parcelSize ??
                              row.parcelItem.deliveryBasis,
                            LPG_UNIT
                          )}
                      </td>
                      <td
                        className={`${cellClass} align-top`}
                        style={nestedCol.alpha}
                      >
                        {(() => {
                          const ratioKey = getCargoRatioKey(row.ratio?.id);
                          const ratioEntry = (
                            row.parcelItem.counterCargoRatios || []
                          ).find(
                            ratio =>
                              String(
                                ratio.quantityInformationCargoRatioFK ?? ''
                              ) ===
                                String(
                                  row.parcelItem
                                    .quantityInformationCargoRatioId ?? ''
                                ) || getCargoRatioKey(ratio.id) === ratioKey
                          );
                          const counterAlpha =
                            ratioEntry?.alpha ??
                            row.parcelItem.counterRatioAlphaByKey?.[ratioKey] ??
                            '';
                          const pricingRow = {
                            ...row.parcelItem,
                            cargoRatio:
                              row.ratio?.id ?? row.parcelItem.cargoRatio,
                            counterCargoRatioId: row.ratio?.id ?? null,
                            counterCargoRatioLabel: row.ratio?.label ?? null,
                            counterCargoRatioRecordId:
                              ratioEntry?.recordId ??
                              row.ratio?.recordId ??
                              null,
                            quantityInformationCargoRatioId:
                              ratioEntry?.quantityInformationCargoRatioFK ??
                              row.ratio?.quantityInformationCargoRatioFK ??
                              row.parcelItem.quantityInformationCargoRatioId ??
                              null,
                            counterAlpha,
                          };
                          const pricingRowKey = getRowKey(pricingRow);
                          return renderNumberInput({
                            rowKey: pricingRowKey,
                            row: pricingRow,
                            field: 'counterAlpha',
                            value: pricingRow.counterAlpha,
                            disabled: !isDeliveryEditable,
                            suffix: 'USD/MT',
                          });
                        })()}
                      </td>
                      {row.showGroupCells && isDeliveryEditable && (
                        <td
                          rowSpan={row.groupRowSpan}
                          className='text-center py-2 pe-1 align-top'
                          style={nestedCol.counter}
                        >
                          <div className='d-flex justify-content-center gap-1 flex-wrap'>
                            <ActionButton
                              variant='icon'
                              size='sm'
                              icon='ri-edit-line'
                              label='Edit Counter Offer'
                              style={COUNTER_ICON_BTN_STYLE}
                              onClick={() => openCounterModal(data)}
                            />
                            <ActionButton
                              variant='icon'
                              size='sm'
                              danger
                              confirm={false}
                              icon='ri-delete-bin-line'
                              label='Delete Counter Offer'
                              onClick={() => handleDeleteCounter(data)}
                            />
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  const renderNoCounterOfferRow = () => (
    <tr className='bg-light'>
      <td colSpan={colSpan} className='p-0' style={COUNTER_ROW_MARK_STYLE}>
        <div className='px-4 py-4 text-muted fs-13 text-center'>
          There is no counteroffer for the requirement mentioned above.
        </div>
      </td>
    </tr>
  );

  return (
    <>
      <CardBody className={embedded ? 'p-0' : 'px-0'}>
        {!embedded && (
          <div className='d-flex align-items-center mb-3'>
            <div
              className='position-relative'
              style={{ width: '280px', maxWidth: '100%' }}
            >
              <i className='ri-search-line position-absolute top-50 translate-middle-y text-muted ms-2' />
              <Input
                type='text'
                placeholder='Search'
                className='ps-5 rounded-3'
                style={{ height: '36px' }}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        )}

        <div
          className={
            embedded
              ? 'table-responsive w-100'
              : 'table-responsive table-card border rounded-3 w-100'
          }
        >
          <table
            aria-label='Delivery offers'
            className='table table-hover table-centered align-middle mb-0 w-100'
            style={DELIVERY_MAIN_TABLE_STYLE}
          >
            <thead className='table-light'>
              <tr>
                <th
                  className={headerCellClass}
                  style={withHeaderStyle(col.expand)}
                >
                  <span className='visually-hidden'>Expand</span>
                </th>
                <th
                  className={headerCellClass}
                  style={withHeaderStyle(col.incoterm)}
                >
                  <DeliveryThLabel
                    title='Incoterm - Route'
                    lines={['Incoterm', 'Route']}
                  />
                </th>
                <th
                  className={headerCellClass}
                  style={withHeaderStyle(col.port)}
                >
                  Port 1
                </th>
                <th
                  className={headerCellClass}
                  style={withHeaderStyle(col.port)}
                >
                  Port 2
                </th>
                <th
                  className={headerCellClass}
                  style={withHeaderStyle(col.port)}
                >
                  Port 3
                </th>
                {requiresCargoRatio && (
                  <th
                    className={headerCellClass}
                    style={withHeaderStyle(col.cargoRatio)}
                  >
                    <DeliveryThLabel
                      title='Cargo Ratio'
                      lines={['Cargo', 'Ratio']}
                    />
                  </th>
                )}
                <th
                  className={headerCellClass}
                  style={withHeaderStyle(col.parcel)}
                >
                  <DeliveryThLabel
                    title='Parcel Size'
                    lines={['Parcel', 'Size']}
                  />
                </th>
                <th
                  className={headerCellClass}
                  style={withHeaderStyle(col.capable)}
                >
                  <DeliveryThLabel
                    title='Is Capable?'
                    lines={['Is', 'Capable?']}
                  />
                </th>
                <th
                  className={headerCellClass}
                  style={withHeaderStyle(col.alpha)}
                >
                  Alpha
                </th>
                {showCounterOfferColumn && (
                  <th
                    className={headerCellClass}
                    style={withHeaderStyle(col.counter)}
                  >
                    <DeliveryThLabel
                      title='Counter Offer'
                      lines={['Counter', 'Offer']}
                    />
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <SkeletonTable inline rows={3} columns={colSpan} />
              ) : tableRows.length > 0 ? (
                tableRows.map(row => {
                  const { data, groupId, groupMeta } = row;
                  const rowKey = getRowKey(data);
                  const isCapable = capableState[rowKey] || false;
                  const isExpanded = expandedRows[groupId];
                  const showCounter = hasCounterOffer(data);
                  const groupHasCounter = !!counterByGroupId[groupId];
                  const groupRows = tableRows.filter(
                    r => r.groupId === groupId
                  );
                  const groupAllCapable = groupRows.every(
                    r => capableState[getRowKey(r.data)] || false
                  );
                  const showCounterButton =
                    row.showGroupCells &&
                    showCounterOfferColumn &&
                    !groupHasCounter &&
                    !groupAllCapable;

                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        className={
                          isExpanded && row.showGroupCells
                            ? 'border-bottom-0'
                            : undefined
                        }
                      >
                        {row.showGroupCells && (
                          <td
                            rowSpan={row.groupRowSpan}
                            className={cellClass}
                            style={{
                              ...col.expand,
                              ...(groupHasCounter
                                ? COUNTER_ROW_MARK_STYLE
                                : null),
                            }}
                          >
                            <button
                              type='button'
                              className='btn btn-link btn-sm p-0 text-muted'
                              onClick={() => toggleRowExpand(groupId)}
                              aria-expanded={isExpanded}
                              aria-label={
                                isExpanded ? 'Collapse row' : 'Expand row'
                              }
                            >
                              <i
                                className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line fs-16`}
                                aria-hidden='true'
                              />
                            </button>
                          </td>
                        )}

                        {row.showGroupCells && (
                          <td
                            rowSpan={row.groupRowSpan}
                            className={`${cellClass} fs-13`}
                            style={col.incoterm}
                          >
                            {groupMeta.incotermRoute}
                          </td>
                        )}

                        {row.showGroupCells && (
                          <td
                            rowSpan={row.groupRowSpan}
                            className={cellClass}
                            style={col.port}
                          >
                            {renderLpgPortCell(
                              groupMeta.port1,
                              groupMeta.parcelSizes,
                              {
                                portLabel: 'Location',
                                startDate: groupMeta.port1StartDate,
                                endDate: groupMeta.port1EndDate,
                                fallbackDate: groupMeta.addAld,
                                locationNames: groupMeta.port1Names,
                                route: groupMeta.route,
                              }
                            )}
                          </td>
                        )}

                        {row.showGroupCells && (
                          <td
                            rowSpan={row.groupRowSpan}
                            className={cellClass}
                            style={col.port}
                          >
                            {renderLpgPortCell(
                              groupMeta.port2,
                              groupMeta.port2 ? groupMeta.parcelSizes : [],
                              {
                                startDate: groupMeta.port2StartDate,
                                endDate: groupMeta.port2EndDate,
                                fallbackDate: groupMeta.addAld2,
                              }
                            )}
                          </td>
                        )}

                        {row.showGroupCells && (
                          <td
                            rowSpan={row.groupRowSpan}
                            className={cellClass}
                            style={col.port}
                          >
                            {renderLpgPortCell(
                              groupMeta.port3,
                              groupMeta.port3 ? groupMeta.parcelSizes : [],
                              {
                                startDate: groupMeta.port3StartDate,
                                endDate: groupMeta.port3EndDate,
                                fallbackDate: groupMeta.addAld3,
                              }
                            )}
                          </td>
                        )}

                        {requiresCargoRatio && row.showCargoRatioCell && (
                          <td
                            rowSpan={row.cargoRatioRowSpan}
                            className={`${cellClass} fs-13`}
                            style={col.cargoRatio}
                          >
                            {row.cargoRatioLabel ? (
                              <span
                                className='badge bg-primary-subtle text-primary border border-primary-subtle fw-semibold rounded-3'
                                style={{ fontSize: '12px' }}
                              >
                                {row.cargoRatioLabel}
                              </span>
                            ) : (
                              <span className='text-muted'>-</span>
                            )}
                          </td>
                        )}

                        <td
                          className={`${cellClass} fs-13 fw-semibold`}
                          style={col.parcel}
                        >
                          {data.parcelSizeLabel ||
                            formatParcelSize(
                              data.parcelSize ?? data.deliveryBasis,
                              LPG_UNIT
                            )}
                        </td>

                        <td
                          className={`${cellClass} text-center`}
                          style={col.capable}
                        >
                          {!isDeliveryEditable ? (
                            isCapable ? (
                              <Badge
                                color='primary'
                                className='px-2 py-1 fs-12 fw-medium rounded-pill'
                              >
                                Compliant
                              </Badge>
                            ) : groupHasCounter ? (
                              <span
                                className='badge rounded-pill px-2 py-1 fs-12 fw-medium'
                                style={{
                                  backgroundColor: COUNTER_ACCENT,
                                  color: '#fff',
                                }}
                              >
                                Countered
                              </span>
                            ) : (
                              <Badge
                                color='secondary'
                                className='px-2 py-1 fs-12 fw-medium rounded-pill'
                              >
                                Not Offered
                              </Badge>
                            )
                          ) : (
                            <div className='form-check form-switch d-flex justify-content-center mb-0'>
                              <Input
                                className='form-check-input'
                                type='checkbox'
                                role='switch'
                                checked={showCounter ? false : isCapable}
                                disabled={showCounter}
                                onChange={e =>
                                  handleCapableChange(
                                    e,
                                    rowKey,
                                    data.quantityInformationDetailId
                                  )
                                }
                              />
                            </div>
                          )}
                        </td>

                        <td className={cellClass} style={col.alpha}>
                          {renderNumberInput({
                            rowKey,
                            row: data,
                            field: 'alpha',
                            value: data.alpha || '',
                            disabled: !isCapable,
                            suffix: 'USD/MT',
                          })}
                        </td>

                        {row.showGroupCells && showCounterOfferColumn && (
                          <td
                            rowSpan={row.groupRowSpan}
                            className={`${cellClass} text-center`}
                            style={col.counter}
                          >
                            {showCounterButton && (
                              <ActionButton
                                variant='icon'
                                size='sm'
                                icon='ri-edit-line'
                                label='Counter Offer'
                                style={COUNTER_ICON_BTN_STYLE}
                                onClick={() =>
                                  openCounterModal(
                                    groupMeta.representative || data
                                  )
                                }
                              />
                            )}
                          </td>
                        )}
                      </tr>

                      {row.isLastInGroup &&
                        isExpanded &&
                        !groupAllCapable &&
                        (groupHasCounter
                          ? renderCounterOfferRow(
                              groupMeta.representative || data,
                              groupMeta
                            )
                          : renderNoCounterOfferRow())}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={colSpan} className='text-center text-muted py-4'>
                    {searchTerm
                      ? 'No data matching your search'
                      : 'No data available'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardBody>

      <CounterOfferModal
        isOpen={counterModalOpen}
        toggle={closeCounterModal}
        formData={counterFormData}
        saveDisabled={!counterHasChanges}
        errors={counterFormErrors}
        onFormChange={handleCounterFormChange}
        onPortSetup={setCounterPortSetup}
        onSave={handleSaveCounter}
        loading={counterFormLoading}
        form={form}
        requiresCargoRatio={requiresCargoRatio}
        incotermTypeOptions={incotermTypeOptions}
        incotermRouteOptions={incotermRouteOptions}
        incotermLocationOptions={incotermLocationOptions}
        fobRouteOptions={fobRouteOptions}
      />
    </>
  );
};

export default ModelDeliveryTableLpg;
